<script setup>
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler.js";
import { useVehicleConfigurator } from "../../Composables/useVehicleConfigurator.js";
import { useRouter } from "vue-router";
import { computed, onMounted, reactive, ref } from "vue";
import Loader from "../../Components/Loader.vue";
import FormTextField from "../../Components/FormTextField.vue";
import FormSelectField from "../../Components/FormSelectField.vue";
import { useToast } from "vue-toast-notification";
import FormTextarea from "../../Components/FormTextarea.vue";
import { useAuthUserStore } from "../../Store/AuthUserStore.js";
import FormNumericField from "../../Components/FormNumericField.vue";
import Editor from "@tinymce/tinymce-vue";
import FormSelectSearchUser from "../../Components/FormSelectSearchUser.vue";
import axios from "axios";
import FormVehicleConfigurator from "../../Components/FormVehicleConfigurator.vue";

const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();
const router = useRouter();
const authUserStore = useAuthUserStore();
const authUser = computed(() => authUserStore.authUser);

const loading = ref(false)
const form = reactive( {
    assigned_to_id: null,
    vehicle_type: null,
    vehicle_brand_id: null,
    vehicle_model_id: null,
    vehicle_version_id: null,
    vehicle_engine_id: null,
    vin: null,
    license_plate: null,
    vehicle_year: null,
    mileage: null,
    notes: null,
    description : null,
})
const errors = ref(null);
const tinyMceLoading = ref(true);

const {
    brands: carBrands,
    models: carModels,
    versions: carVersions,
    engines: carEngines,
    setInitialized,
} = useVehicleConfigurator(form, {
    onError: httpErrorsHandler,
})

onMounted(() => {
    setInitialized();
})

function createClientRequest() {
    loading.value = true;
    let formData = new FormData();

    Object.entries(form).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
            return formData.append(key, value ? 1 : 0);
        }
        if (value !== null && value !== '') {
            formData.append(key, value);
        }
    });

    axios.post("/api/information-requests", formData)
        .then(response => {
            $toast.success('Demande d\'informations créée avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            router.push({ name: 'information-requests.show', params: { id: response.data.data.id } });
        })
        .catch(error => {
            errors.value = error.response.data.errors;
            httpErrorsHandler(error);
            loading.value = false;
        });
}

</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <!--Loader-->
            <div class="card" v-if="loading">
                <div class="card-body">
                    <loader/>
                </div>
            </div>
        </div>
    </div>
    <!--Form-->
    <form v-on:submit.prevent="createClientRequest" v-if="!loading">
        <div class="row">
            <div class="col-lg-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-information-outline me-2 align-middle fs-4"></i>Informations demande</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-12 col-md mb-3">
                                <label for="tinyEditor" class="form-label">Description demande<span class="text-danger"> *</span> </label>
                                <div class="position-relative">
                                    <div class="text-center my-2" v-if="tinyMceLoading">
                                        <div class="spinner-grow text-dark" role="status">
                                        </div>
                                        <br>
                                    </div>
                                    <Editor
                                        id="tinyEditor"
                                        v-model="form.description"
                                        license-key="gpl"
                                        :init="{
                                            base_url: '/tinymce/js/tinymce',
                                            suffix: '.min',
                                            contextmenu: false,
                                            language: 'fr_FR',
                                            menubar: false,
                                            content_style: 'body { font-family: \'Inter\', sans-serif; font-size: 13px; }',
                                            statusbar: false,
                                            plugins: ['lists', 'link', 'autoresize'],
                                            toolbar: 'bold italic underline | bullist numlist | link',
                                            paste_data_images: false,
                                            paste_as_text: false,
                                            autoresize_bottom_margin: 0,
                                            min_height: 250,
                                            max_height: 600,
                                            browser_spellcheck: true,
                                            init_instance_callback: function(editor) {
                                                tinyMceLoading = false;
                                            }
                                        }"
                                    />
                                    <span class="form-text text-muted mt-0">Enter = nouveau paragraphe / Maj + Enter = retour à la ligne.</span>
                                </div>
                            </div>

                            <form-textarea
                                name="notes"
                                label="Notes"
                                :label-hidden="false"
                                @update:field="form.notes = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.notes"
                                bs-class="col-xl-12"
                                :row="2"
                            />


                            <form-select-search-user
                                v-if="authUser.role === 'admin' || authUser.role === 'back_office'"
                                :errors="errors"
                                label="Assigné à"
                                name="assigned_to_id"
                                :required="false"
                                :data="form.assigned_to_id"
                                @update:field="form.assigned_to_id = ($event !== null ? $event.id : null)"
                                bs-class="col-md-12"
                                :disabled="false"
                                :taggable="false"
                                :label-hidden="false"
                                :user-pre-selected="null"
                                placeholder="Sélectionnez un utilisateur"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <!--Vehicle-->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-car me-2 align-middle fs-4"></i>Véhicule concerné</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <form-vehicle-configurator
                                :form="form"
                                :errors="errors"
                                :brands="carBrands"
                                :models="carModels"
                                :versions="carVersions"
                                :engines="carEngines"
                                :show-detailed-link="false"
                            />

                            <form-text-field
                                name="license_plate"
                                label="Immatriculation"
                                :label-hidden="false"
                                @update:field="form.license_plate = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.license_plate"
                                bs-class="col-md-6"
                            />

                            <form-text-field
                                name="vin"
                                label="VIN"
                                :label-hidden="false"
                                @update:field="form.vin = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.vin"
                                bs-class="col-md-6"
                                :show-char-count="true"
                                :max-length="17"
                            />

                            <form-numeric-field
                                name="vehicle_year"
                                label="Année du véhicule"
                                :label-hidden="false"
                                @update:field="form.vehicle_year = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.vehicle_year"
                                bs-class="col-md-6"
                                :step="1"
                                :min="1900"
                            />

                            <form-numeric-field
                                name="mileage"
                                label="Kilométrage"
                                :label-hidden="false"
                                @update:field="form.mileage = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.mileage"
                                bs-class="col-md-6"
                                :step="1"
                                :min="0"
                            />

                            <div class="d-flex gap-2 justify-content-end">
                                <button type="button" class="btn btn-soft-dark" @click="router.back()">Retour</button>
                                <button type="submit" class="btn btn-secondary">Valider</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<style scoped>

</style>
